import type { Logger } from '@mimir/common/types/logger';
import { invariant } from '@mimir/common/util/invariant';

export interface TextureDataSourceTexImageSource {
  sourceType: 'texImageSource',
  texImageSource: TexImageSource,
}

export interface TextureDataSourceBuffer {
  sourceType: 'buffer',
  format: GLenum,
  type: GLenum,
  arrayBuffer: ArrayBufferView
}

export type TextureDataSource = TextureDataSourceBuffer | TextureDataSourceTexImageSource;

export class WebGlTextureWrapper {
  private contextId: number;
  private closed = false;
  constructor(
    private webGlWrapper: WebGlWrapper,
    readonly texture : WebGLTexture,
    readonly width: number,
    readonly height: number,
    private returnResources : () => void,
  ) {
    this.contextId = webGlWrapper.getContextId();
  }

  close(): void {
    if (this.closed) {
      throw new Error('WebGLTextureWrapper is already closed');
    }
    this.closed = true;
    if (this.contextId === this.webGlWrapper.getContextId()) {
      this.returnResources();
    }
  }
}

export class FrameBufferWrapper {
  private contextId: number;
  private expired : boolean = false;
  constructor(
    private webGlWrapper: WebGlWrapper,
    readonly frameBuffer: WebGLFramebuffer,
    readonly width: number,
    readonly height: number,
    private colorAttachment0: WebGlTextureWrapper,
    private returnResources: () => void,
  ) {
    this.contextId = webGlWrapper.getContextId();
  }

  toTexture(): WebGlTextureWrapper {
    this.assertNotMoved();
    this.expired = true;
    return new WebGlTextureWrapper(this.webGlWrapper, this.colorAttachment0.texture, this.width, this.height, this.returnResources);
  }

  close(): void {
    this.assertNotMoved();
    this.expired = true;
    if (this.contextId === this.webGlWrapper.getContextId()) {
      this.returnResources();
    }
  }

  private assertNotMoved() {
    if (this.expired) {
      throw new Error('FrameBufferWrapper is expired. Either close() or toTexture() has been called on it');
    }
  }
}

export class LinkedProgram {
  private uniformCache = new Map<string, WebGLUniformLocation | null>();
  constructor(private program: WebGLProgram) { }

  use(gl: WebGL2RenderingContext) {
    gl.useProgram(this.program);
  }

  getUniformLocation(gl: WebGL2RenderingContext, name: string): WebGLUniformLocation | null {
    const cached = this.uniformCache.get(name);
    if (cached !== undefined) {
      return cached;
    }
    const location = gl.getUniformLocation(this.program, name);
    console.log(`got uniform location for: ${name}: ${location}`);
    this.uniformCache.set(name, location)
    return location;
  }

}

export class WebGlWrapper {
  private gl: WebGL2RenderingContext | null = null;

  private textureCache = new Map<string, WebGLTexture[]>();
  private linkedPrograms = new Map<string, LinkedProgram>();
  private frameBuffers = new Map<string, FramebufferInternal[]>();
  private debug = false;

  private contextId : number = 0;

  constructor(
    private readonly logger: Logger,
    private readonly contextSource: OffscreenCanvas,
  ) {
    this.contextSource.addEventListener('webglcontextlost', () => {
      this.clearContext();
    });
  }

  public getCanvasResolution(): { width: number, height: number } {
    return { width: this.contextSource.width, height: this.contextSource.height }
  }

  public getContextId(): number {
    return this.contextId;
  }


  private clearContext() {
    this.gl = null;
    this.textureCache.clear();
    this.linkedPrograms.clear();
  }

  public getGlContext(): WebGL2RenderingContext {
    if (this.gl === null) {
      this.gl = this.contextSource.getContext('webgl2');
      this.contextId ++;
    }
    invariant(this.gl, 'WebGL2 context should be available');
    return this.gl;
  }

  public throwIfError() {
    const gl = this.gl;
    invariant(gl, 'Validate first');

    if (this.debug) {
      const error = gl.getError();
      if (error === gl.NO_ERROR) {
        return;
      }
      throw new Error(`WebGL error occurred: ${getGlErrorName(gl, error)}`);
    }
  }

  public getTexture(internalFormat: GLint, width: GLint, height: GLint, data: TextureDataSource | null): WebGlTextureWrapper {
    const gl = this.getGlContext();
    const key = textureKey(internalFormat, width, height);

    let texture : WebGLTexture;
    let textures = this.textureCache.get(key);
    if ((textures === undefined) || (textures.length === 0)) {
      this.logger.log({
        level: 'debug',
        message: 'Creating new texture',
        width,
        height,
        internalFormat,
      });
      texture = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, texture);
      if (data === null) {
        gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      } else if (data.sourceType === 'texImageSource') {
        gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, gl.RGBA, gl.UNSIGNED_BYTE, data.texImageSource);
      } else {
        gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, width, height, 0, data.format, data.type, data.arrayBuffer);
      }
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);

      /*
        We use CLAMP_TO_EDGE to avoid wraparound artifacts along the edges of the
        rescaled image.
      */
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.bindTexture(gl.TEXTURE_2D, null);
    } else {
      texture = textures.splice(0, 1)[0];
      if (data !== null) {
        gl.bindTexture(gl.TEXTURE_2D, texture);
        switch (data.sourceType) {
          case 'texImageSource':
            /*
              Note that under normal circumstances it is more efficient to run
              texSubImage2D when re-using a texture because this does not
              allocate new internal buffers, but for TexImageSource there seems
              to be a suboptimal code in texSubImage2D which is why we use
              texImage2D even when updating an existing texture.
            */
            gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, gl.RGBA, gl.UNSIGNED_BYTE, data.texImageSource);
            break;
          case 'buffer':
            gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, width, height, data.format, data.type, data.arrayBuffer);
            break;
        }
        gl.bindTexture(gl.TEXTURE_2D, null);
      }
    }

    return new WebGlTextureWrapper(
      this,
      texture,
      width,
      height,
      () => this.returnTexture(internalFormat, width, height, texture),
    );
  }

  returnTexture(internalFormat: GLint, width: GLint, height: GLint, texture: WebGLTexture) {
    const key = textureKey(internalFormat, width, height);

    const textures = this.textureCache.get(key);
    if (textures === undefined) {
      this.textureCache.set(key, [texture]);
    } else {
      textures.push(texture);
    }
  }

  public getLinkedProgram(vertexShaderSource: string, fragmentShaderSource: string): LinkedProgram {
    const key = `${vertexShaderSource}:${fragmentShaderSource}`;
    const existingProgram = this.linkedPrograms.get(key);
    if (existingProgram !== undefined) {
      return existingProgram;
    }

    console.log(`Compiling vertex shader:\n${vertexShaderSource}`);

    const gl = this.getGlContext();
    const vertexShader = gl.createShader(gl.VERTEX_SHADER);
    invariant(vertexShader, 'Vertex shader should be created successfully');
    gl.shaderSource(vertexShader, vertexShaderSource);
    gl.compileShader(vertexShader);
    let vertexStatus = gl.getShaderParameter(vertexShader, gl.COMPILE_STATUS);
    if (!vertexStatus) {
      const infoLog = gl.getShaderInfoLog(vertexShader);
      throw new Error(`Failed to compile vertex shader:\n${vertexShaderSource}\nInfoLog: ${infoLog}`);
    }

    console.log(`Compiling vertex shader:\n${fragmentShaderSource}`);

    const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER);
    invariant(fragmentShader, 'Fragment shader should be created successfully');
    gl.shaderSource(fragmentShader, fragmentShaderSource);
    gl.compileShader(fragmentShader);
    let fragmentStatus = gl.getShaderParameter(fragmentShader, gl.COMPILE_STATUS);
    if (!fragmentStatus) {
      const infoLog = gl.getShaderInfoLog(fragmentShader);
      throw new Error(`Failed to compile fragment shader:\n${fragmentShaderSource}\nInfoLog: ${infoLog}`);
    }

    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    const linkStatus = gl.getProgramParameter(program, gl.LINK_STATUS);
    if (!linkStatus) {
      const linkInfo = gl.getProgramInfoLog(program);
      throw new Error(`Program linking failed: ${linkInfo}`);
    }

    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);

    const linkedProgram = new LinkedProgram(program);
    this.linkedPrograms.set(key, linkedProgram);
    return linkedProgram;
  }

  public getFrameBuffer(width: number, height: number) {
    const key = frameBufferKey(width, height);

    let frameBufferInternal : FramebufferInternal;

    const frameBuffers = this.frameBuffers.get(key);

    if ((frameBuffers === undefined) || (frameBuffers.length === 0)) {
      console.warn(`Creating new frame buffer: ${width}x${height}`);
      const gl = this.getGlContext();
      const frameBuffer = gl.createFramebuffer();
      const textureWrapper = this.getTexture(gl.RGBA, width, height, null);
      gl.bindFramebuffer(gl.FRAMEBUFFER, frameBuffer);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, textureWrapper.texture, 0);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);

      frameBufferInternal = {
        frameBuffer,
        colorAttachment0: textureWrapper,
      };
    } else {
      frameBufferInternal = frameBuffers.splice(0, 1)[0];
    }

    return new FrameBufferWrapper(
      this,
      frameBufferInternal.frameBuffer,
      width,
      height,
      frameBufferInternal.colorAttachment0,
      () => {
        this.returnFrameBuffer(width, height, frameBufferInternal);
      }
    );
  }

  public returnFrameBuffer(width: number, height: number, frameBufferInternal: FramebufferInternal) {
    const key = frameBufferKey(width, height);
    const frameBuffers = this.frameBuffers.get(key);
    if (frameBuffers === undefined) {
      this.frameBuffers.set(key, [frameBufferInternal]);
    } else {
      frameBuffers.push(frameBufferInternal);
    }
  }
}


function textureKey(internalFormat: GLint, width: GLint, height: GLint) {
  return `${internalFormat}:${width}:${height}`;
}

function getGlErrorName(gl: WebGL2RenderingContext, error: number): string {
  switch (error) {
    case gl.INVALID_OPERATION:
      return 'INVALID_OPERATION';
    case gl.OUT_OF_MEMORY:
      return 'OUT_OF_MEMORY';
  }
  return '0x' + error.toString(16).toUpperCase().padStart(4, '0');
}

function frameBufferKey(width: number, height: number): string {
  return `${width}:${height}`;
}

interface FramebufferInternal {
  frameBuffer: WebGLFramebuffer,
  colorAttachment0: WebGlTextureWrapper,
}

